/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Models */
import { DsCompanyGroup } from '../models/ds-company-group';
import { DsCompany } from '../models/ds-company';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Interfaces */
import { DsCompanyInterface } from '../interfaces/ds-company.interface';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Functions */
import { BydGenerateParams } from '../../../functions/byd-generate-params.function';
import { DsCompanyExportModel } from '../models/ds-company-export.model';
import { FilterQueryModel } from '../models/filter-query.model';
import { PageModel } from '../../../shared/models/page.model';

@Injectable({
  providedIn: 'root',
})
export class DsCompaniesService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * The endpoint base
   */
  private basePath = `${this.bydFeatureFlagService.hasFlags('dscompany_path') ? '/dscompany' : ''}/v1/projects/`;

  /**
   * Get companies.
   *
   * @param projectId project ID
   * @param elementsPerPage the element number per page
   * @param pageIndex the page index
   * @param search the search value
   * @param orderBy the orderBy
   * @param orderSort the orderSort
   * @param forCurrentUser Whether to take into account user rights
   * @returns Observable type `<BydModel<DsCompany[]>>`
   */
  public getCompanies(
    projectId: number,
    elementsPerPage = 10,
    pageIndex = 0,
    search = '',
    orderBy = '',
    orderSort: '' | 'asc' | 'desc' = '',
    forCurrentUser?: boolean
  ): Observable<BydModel<DsCompany>> {
    this.path = `${this.basePath}${projectId}/companies`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    switch (orderBy) {
      case 'name':
        orderBy = 'entreprises_nom';
        break;
      case 'quickName':
        orderBy = 'quickName';
        break;
      case 'site':
        orderBy = 'entreprises_http';
        break;
      default:
        orderBy = '';
    }
    const params = BydGenerateParams({
      search,
      forCurrentUser,
      orderBy,
      orderSort,
    });
    return this.getModels<DsCompany>({ headers, params });
  }

  /**
   * Get companies with groups.
   *
   * @param projectId project ID
   * @param pageCompany - PageModel that contains elementPerPage & pageIndex.
   * @param filters - Company filters object.
   * @returns Observable<BydModel<DsCompany>>
   */
  public getCompaniesWithGroups(
    projectId: number,
    pageCompany: PageModel,
    filters: FilterQueryModel
  ): Observable<BydModel<DsCompany>> {
    this.path = `${this.basePath}${projectId}/companies`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(
      pageCompany.elementsPerPage,
      pageCompany.pageIndex
    );
    const params = BydGenerateParams(filters);
    return this.getModels<DsCompany>({ headers, params });
  }

  /**
   * Get companies without pagination.
   *
   * @param projectId project ID
   * @param pageCompany - PageModel that contains elementPerPage & pageIndex.
   * @param filters - Company filters object.
   * @returns Observable type `<BydModel<DsCompanyExportModel[]>>`
   */
  public getCompaniesWithColumns(
    projectId: number,
    pageCompany: PageModel,
    filters: FilterQueryModel
  ): Observable<BydModel<DsCompanyExportModel>> {
    this.path = `/dscompany/v2/projects/${projectId}/companies`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(
      pageCompany.elementsPerPage,
      pageCompany.pageIndex
    );
    const params = BydGenerateParams(filters);
    return this.getModels<DsCompanyExportModel>({ headers, params });
  }

  /**
   * Get Company by id.
   *
   * @param projectId Project ID
   * @param companyId Company ID
   * @returns Observable type `<DsCompany>`
   */
  public getCompany(projectId: number, companyId: number): Observable<DsCompany> {
    this.path = `${this.basePath}${projectId}/companies/${companyId}`;
    return this.getModel<DsCompany>();
  }

  /**
   * Add a new company to the selected project.
   *
   * @param projectId Current project id
   * @param company New company object
   * @param groups company's newly-created groups
   * @param lots company's newly-created lots
   * @returns Observable<number>
   */
  public addCompany(
    projectId: number,
    company: DsCompany,
    groups: DsCompanyGroup[],
    lots: string[]
  ): Observable<number> {
    const body = { company, groups, lots };
    this.path = `${this.basePath}${projectId}/companies`;
    return this.post<number>({ body });
  }

  /**
   * Edit an existing company.
   *
   * @param projectId Current project id
   * @param company Edited company
   * @param groups Edited company groups with group links
   * @param lots company's newly-created lots
   * @returns Observable<boolean>
   */
  public editCompany(
    projectId: number,
    company: DsCompany,
    groups: DsCompanyGroup[],
    lots: string[]
  ): Observable<boolean> {
    const body = { company, groups, lots };
    this.path = `${this.basePath}${projectId}/companies/${company.id}`;
    return this.put<boolean>({ body });
  }

  /**
   * Change the project's printing company.
   *
   * @param projectId Current project id
   * @param companyId The new project's printing company id
   * @returns Observable<boolean>
   */
  public editPrintingCompany(projectId: number, companyId: number) {
    this.path = `${this.basePath}${projectId}/companies/${companyId}/reprography`;
    return this.post<boolean>();
  }

  /**
   * Delete a company by id.
   *
   * @param projectId Current project id
   * @param companyId Id of the company to delete
   * @returns Observable<boolean>
   */
  public deleteCompany(projectId: number, companyId: number): Observable<boolean> {
    this.path = `${this.basePath}${projectId}/companies/${companyId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsCompany instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsCompany` instance.
   */
  public createNewInstance(jsonObject: DsCompanyInterface): DsCompany {
    return new DsCompany(jsonObject);
  }
}
